// Passkey step-up + enrollment modals. Mirrors ConfirmDialog: an
// in-DOM modal over a dimmed backdrop, focus-trapped, dismissable by Escape /
// backdrop. The primary button runs a WebAuthn ceremony; the modal stays open
// on failure so the user can retry, and resolves true only on success.
//
// Desktop-only by design (: sensitive ops are desktop-only). On a
// phone/tablet — or a browser without passkey support — the action variant is
// replaced by an information-only variant with no ceremony button. This is the
// dual-UX adaptation, not a stretched layout: the desktop and phone renders are
// structurally different components for the same step.

import { ApiError } from '../services/api';
import { getDeviceClass } from '../utils/viewport';
import { enrollPasskey, isPasskeySupported, isStepUpRequired, stepUpWithPasskey } from '../services/webauthn';
import { t } from '../i18n';
import { passkeyRefusalCopy } from './passkeyRefusal';

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

// A monotonic counter for the per-instance ARIA ids. Kept module-level so both
// dialog builders and both modal wrappers draw from one sequence — two dialogs
// open at once (or a catalog rendering both shapes) never collide on an id.
let uid = 0;

// buildStepUpDialog / buildInfoDialog construct the real .passkey__dialog node
// — the exact DOM the modals render — WITHOUT the .passkey overlay wrapper
// (issue 407). The overlay is position:fixed inset:0, so the catalog fixture
// must mount the dialog inline or it would cover the viewport and destroy every
// other section's baseline. The modals below append the returned dialog to
// their overlay and drive it; the catalog appends it to a section. One
// construction, two mounts — so the axe gate sees exactly what ships, not a
// hand-built replica that could drift.
//
// Construction-only by contract (issue 407 AC.2 / security note): these emit
// DOM and nothing else — no overlay behaviour, no ceremony, no focus trap, no
// resolution. Every byte of the node (classes, role, the per-instance
// aria-labelledby/describedby from issue 390) is identical to what the modals
// built inline before.
interface StepUpDialogParts {
  dialog: HTMLDivElement;
  cancel: HTMLButtonElement;
  action: HTMLButtonElement;
  error: HTMLParagraphElement;
}

export function buildStepUpDialog(spec: Pick<ActionSpec, 'title' | 'message' | 'actionLabel' | 'cancelLabel'>): StepUpDialogParts {
  uid += 1;
  const titleId = `passkey-title-${uid}`;
  const descId = `passkey-desc-${uid}`;

  const dialog = document.createElement('div');
  dialog.className = 'passkey__dialog';
  dialog.setAttribute('role', 'alertdialog');
  dialog.setAttribute('aria-modal', 'true');
  dialog.setAttribute('aria-labelledby', titleId);
  dialog.setAttribute('aria-describedby', descId);

  const title = document.createElement('h2');
  title.className = 'passkey__title';
  title.id = titleId;
  title.textContent = spec.title;

  const message = document.createElement('p');
  message.className = 'passkey__message';
  message.id = descId;
  message.textContent = spec.message;

  const error = document.createElement('p');
  error.className = 'passkey__error';
  // role="alert" so unhiding it announces: the failure copy is the whole feedback
  // for a ceremony the user cannot see, and a silently-revealed <p> tells a screen
  // reader nothing.
  error.setAttribute('role', 'alert');
  error.hidden = true;

  const actions = document.createElement('div');
  actions.className = 'passkey__actions';

  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'btn btn--secondary passkey__btn passkey__btn--cancel';
  cancel.textContent = spec.cancelLabel;

  const action = document.createElement('button');
  action.type = 'button';
  action.className = 'btn btn--primary passkey__btn passkey__btn--action';
  action.textContent = spec.actionLabel;

  actions.append(cancel, action);
  dialog.append(title, message, error, actions);
  return { dialog, cancel, action, error };
}

export function buildInfoDialog(spec: InfoSpec): { dialog: HTMLDivElement; dismiss: HTMLButtonElement } {
  uid += 1;
  const titleId = `passkey-info-title-${uid}`;
  const descId = `passkey-info-desc-${uid}`;

  const dialog = document.createElement('div');
  dialog.className = 'passkey__dialog';
  dialog.setAttribute('role', 'alertdialog');
  dialog.setAttribute('aria-modal', 'true');
  dialog.setAttribute('aria-labelledby', titleId);
  dialog.setAttribute('aria-describedby', descId);

  const title = document.createElement('h2');
  title.className = 'passkey__title';
  title.id = titleId;
  title.textContent = spec.title;

  const message = document.createElement('p');
  message.className = 'passkey__message';
  message.id = descId;
  message.textContent = spec.message;

  const actions = document.createElement('div');
  actions.className = 'passkey__actions';

  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.className = 'btn btn--primary passkey__btn passkey__btn--action';
  dismiss.textContent = spec.dismissLabel;

  actions.append(dismiss);
  dialog.append(title, message, actions);
  return { dialog, dismiss };
}

interface ActionSpec {
  title: string;
  message: string;
  actionLabel: string;
  progressLabel: string;
  cancelLabel: string;
  run: () => Promise<void>;
}

interface InfoSpec {
  title: string;
  message: string;
  dismissLabel: string;
}

// requestPasskeyStepUp prompts the user to verify with their passkey. Resolves
// true once the assertion succeeds (the session is now stepped up), false if
// the user dismisses. On a non-desktop / unsupported client it shows why the
// operation can't proceed and resolves false.
export function requestPasskeyStepUp(): Promise<boolean> {
  if (getDeviceClass() !== 'desktop' || !isPasskeySupported()) {
    return infoModal({
      title: t('passkeyModal.desktopRequired.title'),
      message: t('passkeyModal.desktopRequired.message'),
      dismissLabel: t('passkeyModal.ok'),
    });
  }
  return actionModal({
    title: t('passkeyModal.stepUp.title'),
    message: t('passkeyModal.stepUp.message'),
    actionLabel: t('passkeyModal.stepUp.action'),
    progressLabel: t('passkeyModal.stepUp.progress'),
    cancelLabel: t('common.cancel'),
    run: stepUpWithPasskey,
  });
}

// promptPasskeyEnroll offers to set up a passkey on first desktop sign-in for a
// sensitive role. Resolves true once enrolled, false if declined or not on a
// desktop (phones are exempt — no prompt).
export function promptPasskeyEnroll(): Promise<boolean> {
  if (getDeviceClass() !== 'desktop' || !isPasskeySupported()) {
    return Promise.resolve(false);
  }
  return actionModal({
    title: t('passkeyModal.enroll.title'),
    message: t('passkeyModal.enroll.message'),
    actionLabel: t('passkeyModal.enroll.action'),
    progressLabel: t('passkeyModal.enroll.progress'),
    cancelLabel: t('passkeyModal.enroll.cancel'),
    run: enrollPasskey,
  });
}

// withPasskeyStepUp wraps a sensitive API call: if the server gates it with
// 401 WEBAUTHN_STEP_UP_REQUIRED, it runs the assertion ceremony and retries
// once. Any other error — or a dismissed prompt — propagates unchanged. This is
// the call site every certification / export / AI-report action will use.
//
// opts.onStepUp fires only on the ceremony branch (a fresh assertion was run),
// letting a caller tell the two success paths apart: a call that passed
// straight through (a live step-up window, no ceremony) vs one that had to
// re-assert. Purely additive — callers that omit it are unaffected.
export async function withPasskeyStepUp<T>(
  call: () => Promise<T>,
  opts?: { onStepUp?: () => void },
): Promise<T> {
  try {
    return await call();
  } catch (err) {
    if (!isStepUpRequired(err)) throw err;
    const ok = await requestPasskeyStepUp();
    if (!ok) throw err;
    opts?.onStepUp?.();
    return await call();
  }
}

function actionModal(spec: ActionSpec): Promise<boolean> {
  const previouslyFocused = document.activeElement as HTMLElement | null;

  const overlay = document.createElement('div');
  overlay.className = 'passkey';

  const { dialog, cancel, action, error } = buildStepUpDialog(spec);
  overlay.append(dialog);

  return new Promise<boolean>((resolve) => {
    let settled = false;
    let running = false;

    function close(result: boolean): void {
      if (settled) return;
      settled = true;
      document.removeEventListener('keydown', onKeydown, true);
      overlay.remove();
      if (previouslyFocused && document.contains(previouslyFocused)) previouslyFocused.focus();
      resolve(result);
    }

    async function onAction(): Promise<void> {
      if (running) return;
      running = true;
      error.hidden = true;
      action.disabled = true;
      cancel.disabled = true;
      action.textContent = spec.progressLabel;
      try {
        await spec.run();
        close(true);
      } catch (err) {
        running = false;
        action.disabled = false;
        cancel.disabled = false;
        action.textContent = spec.actionLabel;
        error.textContent = ceremonyError(err);
        error.hidden = false;
      }
    }

    function onKeydown(e: KeyboardEvent): void {
      if (e.key === 'Escape' && !running) {
        e.preventDefault();
        close(false);
        return;
      }
      if (e.key === 'Tab') trapTab(e, dialog);
    }

    cancel.addEventListener('click', () => {
      if (!running) close(false);
    });
    action.addEventListener('click', () => void onAction());
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay && !running) close(false);
    });
    document.addEventListener('keydown', onKeydown, true);

    document.body.append(overlay);
    action.focus();
  });
}

function infoModal(spec: InfoSpec): Promise<boolean> {
  const previouslyFocused = document.activeElement as HTMLElement | null;

  const overlay = document.createElement('div');
  overlay.className = 'passkey';
  overlay.dataset.variant = 'info';

  const { dialog, dismiss } = buildInfoDialog(spec);
  overlay.append(dialog);

  return new Promise<boolean>((resolve) => {
    let settled = false;
    function close(): void {
      if (settled) return;
      settled = true;
      document.removeEventListener('keydown', onKeydown, true);
      overlay.remove();
      if (previouslyFocused && document.contains(previouslyFocused)) previouslyFocused.focus();
      resolve(false);
    }
    function onKeydown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      } else if (e.key === 'Tab') {
        trapTab(e, dialog);
      }
    }
    dismiss.addEventListener('click', close);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    });
    document.addEventListener('keydown', onKeydown, true);
    document.body.append(overlay);
    dismiss.focus();
  });
}

function trapTab(e: KeyboardEvent, dialog: HTMLElement): void {
  const focusables = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !(el as HTMLButtonElement).disabled,
  );
  if (focusables.length === 0) return;
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const activeEl = document.activeElement as HTMLElement | null;
  if (e.shiftKey && activeEl === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && activeEl === last) {
    e.preventDefault();
    first.focus();
  }
}

function ceremonyError(err: unknown): string {
  if (err instanceof DOMException) {
    if (err.name === 'NotAllowedError') return t('passkeyModal.error.cancelled');
    if (err.name === 'InvalidStateError') return t('passkeyModal.error.exists');
    return t('passkeyModal.error.authenticator');
  }
  if (err instanceof ApiError) {
    const named = passkeyRefusalCopy(err);
    if (named) return named;
    const body = err.body as { error?: string } | null;
    return body?.error ?? t('passkeyModal.error.verifyFailed');
  }
  return t('passkeyModal.error.generic');
}
